import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Alert,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText
} from 'reactstrap';
import { getStockByProduct } from './Api/GmsStock';
import { 
  getStockSelectionLocks, 
  insertStockSelectionLock, 
  deleteStockSelectionLock 
} from './Api/GmsStockSelectionLock';
import { Search, Package } from 'lucide-react';

const StockSelectionModal = ({ 
  isOpen, 
  toggle, 
  productId, 
  productName, 
  requestedQuantity, 
  onStockSelected,
  selectedStockItems = [],
  branchId = null,
  businessId = null,
  preloadedStockItems = null,
  showCostPriceColumn = false,
  customFetchStock = null,
  temporaryFormUniqueId = null,
  flag = null,
  createdBy = null
}) => {
  const [stockItems, setStockItems] = useState([]);
  const [filteredStockItems, setFilteredStockItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [displayCount, setDisplayCount] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [lockedStockIds, setLockedStockIds] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [lockedStocksLoaded, setLockedStocksLoaded] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(50);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Calculate initial display count (5x requested quantity)
  const initialDisplayCount = Math.max(10, requestedQuantity * 5);

  const isFractionalProduct = stockItems.some(item => 
    item.unit_AllowFractional || item.unitAllowFractional || item.Unit_AllowFractional
  );

  // Helper function to parse DD/MM/YYYY format date string
  const parseDDMMYYYY = (dateStr) => {
    if (!dateStr) return null;
    const ddMMyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(ddMMyyyyPattern);
    if (match) {
      const [, day, month, year] = match;
      return new Date(`${year}-${month}-${day}`);
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Fetch locked stocks to filter them out
  const fetchLockedStocks = async () => {
    try {
      const response = await getStockSelectionLocks(productId, temporaryFormUniqueId, null);
      if (response.isSuccess && response.lockedStocks) {
        const lockedIds = response.lockedStocks.map(lock => lock.purchaseStock_Id);
        setLockedStockIds(lockedIds);
        setLockedStocksLoaded(true);
      } else {
        setLockedStockIds([]);
        setLockedStocksLoaded(true);
      }
    } catch (error) {
      console.error('❌ Error fetching locked stocks:', error);
      setLockedStockIds([]);
      setLockedStocksLoaded(true);
    }
  };

  useEffect(() => {
    if (isOpen && productId) {
      // Reset state immediately when modal opens or productId changes
      setSelectedItems([]);
      setSelectedQuantities({});
      setStockItems([]);
      setFilteredStockItems([]);
      setLockedStockIds([]);
      setAlerts([]);
      setDismissedAlerts(new Set());
      setLockedStocksLoaded(false);
      setLoading(true);
      setSearchTerm('');
      setDisplayCount(initialDisplayCount);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setHasMorePages(false);
      setIsLoadingMore(false);
      
      const initializeModal = async () => {
        let currentLockedIds = [];
        
        if (temporaryFormUniqueId && flag) {
          const response = await getStockSelectionLocks(productId, temporaryFormUniqueId, null);
          if (response.isSuccess && response.lockedStocks) {
            currentLockedIds = response.lockedStocks.map(lock => lock.purchaseStock_Id);
            setLockedStockIds(currentLockedIds);
            setLockedStocksLoaded(true);
          } else {
            setLockedStockIds([]);
            setLockedStocksLoaded(true);
          }
        } else {
          setLockedStocksLoaded(true);
        }
        
        // Load initial selected items if any passed from parent
        if (selectedStockItems && selectedStockItems.length > 0) {
          const initialSelectedIds = [];
          const initialSelectedQuantities = {};
          selectedStockItems.forEach(group => {
            if (group.stocks) {
              group.stocks.forEach(stock => {
                const id = stock.purchaseStock_Id || stock.stockTransaction_StockId;
                if (id) {
                  initialSelectedIds.push(id);
                  initialSelectedQuantities[id] = stock.selectedQty || 1;
                }
              });
            }
          });
          if (initialSelectedIds.length > 0) {
            setSelectedItems(initialSelectedIds);
            setSelectedQuantities(initialSelectedQuantities);
          }
        }
        
        if (preloadedStockItems) {
          const unlockedPreloadedItems = preloadedStockItems.filter(item => 
            !currentLockedIds.includes(item.purchaseStock_Id)
          );
          setStockItems(unlockedPreloadedItems);
          setDisplayCount(initialDisplayCount);
          setLoading(false);
        } else {
          await fetchStockDataWithLocks(currentLockedIds, 1, null, false);
        }
      };
      
      initializeModal();
    } else if (!isOpen) {
      setSelectedItems([]);
      setSelectedQuantities({});
      setStockItems([]);
      setFilteredStockItems([]);
      setLockedStockIds([]);
      setAlerts([]);
      setDismissedAlerts(new Set());
      setLockedStocksLoaded(false);
      setLoading(false);
      setSearchTerm('');
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setHasMorePages(false);
      setIsLoadingMore(false);
    }
  }, [isOpen, productId, preloadedStockItems, temporaryFormUniqueId, flag]);

  // Periodically refresh locked stocks while modal is open
  useEffect(() => {
    if (!isOpen || !temporaryFormUniqueId || !flag) return;
    
    fetchLockedStocks();
    
    const intervalId = setInterval(() => {
      fetchLockedStocks();
    }, 5000);
    
    return () => clearInterval(intervalId);
  }, [isOpen, temporaryFormUniqueId, flag, productId]);

  // Cleanup locks when modal closes
  useEffect(() => {
    return () => {
      if (temporaryFormUniqueId && !isOpen) {
        handleCleanupLocks();
      }
    };
  }, [isOpen, temporaryFormUniqueId]);

  useEffect(() => {
    const filtered = stockItems.filter(item => {
      const isFractional = item.unit_AllowFractional || item.unitAllowFractional || item.Unit_AllowFractional;
      const isNotLocked = isFractional || !lockedStockIds.includes(item.purchaseStock_Id);
      return isNotLocked;
    });
    setFilteredStockItems(filtered);
  }, [stockItems, lockedStockIds]);

  // Server-side search with debounce
  useEffect(() => {
    if (!isOpen || !productId || preloadedStockItems || !lockedStocksLoaded) return;
    
    if (searchTerm === '') {
      return;
    }
    
    const searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      setStockItems([]);
      setFilteredStockItems([]);
      fetchStockDataWithLocks(lockedStockIds, 1, searchTerm);
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, isOpen, productId, preloadedStockItems, lockedStocksLoaded]);

  // Auto-select items based on requested quantity
  useEffect(() => {
    if (filteredStockItems.length > 0 && selectedItems.length === 0 && requestedQuantity > 0 && lockedStocksLoaded) {
      const sortedItems = [...filteredStockItems].sort((a, b) => {
        if (a.purchaseStock_ExpDate && b.purchaseStock_ExpDate) {
          const dateA = parseDDMMYYYY(a.purchaseStock_ExpDate);
          const dateB = parseDDMMYYYY(b.purchaseStock_ExpDate);
          if (dateA && dateB) {
            return dateA - dateB;
          }
          if (dateA) return -1;
          if (dateB) return 1;
        }
        if (a.purchaseStock_ExpDate) return -1;
        if (b.purchaseStock_ExpDate) return 1;
        return 0;
      });

      if (isFractionalProduct) {
        let remaining = requestedQuantity;
        const autoSelectedIds = [];
        const autoSelectedQuantities = {};

        for (const item of sortedItems) {
          if (remaining <= 0) break;
          const remainingQty = item.purchaseStock_RemainingQty || item.purchaseStock_Quantity || 0;
          if (remainingQty <= 0) continue;

          const selectQty = Math.min(remaining, remainingQty);
          autoSelectedIds.push(item.purchaseStock_Id);
          autoSelectedQuantities[item.purchaseStock_Id] = selectQty;
          remaining -= selectQty;
        }

        setSelectedItems(autoSelectedIds);
        setSelectedQuantities(autoSelectedQuantities);

        // Fractional products are not locked to allow concurrent partial consumption
        if (false && temporaryFormUniqueId && flag && createdBy && autoSelectedIds.length > 0) {
          const insertLocksForAutoSelected = async () => {
            try {
              const stockIdsString = autoSelectedIds.join(',');
              const result = await insertStockSelectionLock(
                stockIdsString,
                temporaryFormUniqueId,
                flag,
                createdBy
              );
              
              if (!result.isSuccess && result.message) {
                const alertId = `auto-lock-warning-${Date.now()}-${Math.random()}`;
                setAlerts(prev => {
                  const hasSimilarAlert = prev.some(alert => 
                    alert.message.includes('Some stocks could not be locked') && alert.type === 'warning'
                  );
                  if (!hasSimilarAlert) {
                    setTimeout(() => {
                      setDismissedAlerts(prev => new Set([...prev, alertId]));
                      setAlerts(current => current.filter(alert => alert.id !== alertId));
                    }, 5000);
                    return [...prev, {
                      type: 'warning',
                      message: `Some stocks could not be locked: ${result.message}`,
                      id: alertId
                    }];
                  }
                  return prev;
                });
              }
            } catch (error) {
              console.error('❌ Error locking auto-selected stocks:', error);
              const alertId = `auto-lock-error-${Date.now()}-${Math.random()}`;
              setAlerts(prev => {
                const hasSimilarAlert = prev.some(alert => 
                  alert.message.includes('could not be locked automatically') && alert.type === 'warning'
                );
                if (!hasSimilarAlert) {
                  setTimeout(() => {
                    setDismissedAlerts(prev => new Set([...prev, alertId]));
                    setAlerts(current => current.filter(alert => alert.id !== alertId));
                  }, 5000);
                  return [...prev, {
                    type: 'warning',
                    message: 'Some stocks could not be locked automatically. Please verify availability.',
                    id: alertId
                  }];
                }
                return prev;
              });
            }
          };
          insertLocksForAutoSelected();
        }
      } else {
        const autoSelected = sortedItems.slice(0, requestedQuantity);
        const autoSelectedIds = autoSelected.map(item => item.purchaseStock_Id);
        setSelectedItems(autoSelectedIds);
        
        const autoSelectedQuantities = {};
        autoSelectedIds.forEach(id => {
          autoSelectedQuantities[id] = 1;
        });
        setSelectedQuantities(autoSelectedQuantities);
        
        if (temporaryFormUniqueId && flag && createdBy && autoSelectedIds.length > 0) {
          const insertLocksForAutoSelected = async () => {
            try {
              const stockIdsString = autoSelectedIds.join(',');
              const result = await insertStockSelectionLock(
                stockIdsString,
                temporaryFormUniqueId,
                flag,
                createdBy
              );
              
              if (!result.isSuccess && result.message) {
                const alertId = `auto-lock-warning-${Date.now()}-${Math.random()}`;
                setAlerts(prev => {
                  const hasSimilarAlert = prev.some(alert => 
                    alert.message.includes('Some stocks could not be locked') && alert.type === 'warning'
                  );
                  if (!hasSimilarAlert) {
                    setTimeout(() => {
                      setDismissedAlerts(prev => new Set([...prev, alertId]));
                      setAlerts(current => current.filter(alert => alert.id !== alertId));
                    }, 5000);
                    return [...prev, {
                      type: 'warning',
                      message: `Some stocks could not be locked: ${result.message}`,
                      id: alertId
                    }];
                  }
                  return prev;
                });
              }
            } catch (error) {
              console.error('❌ Error locking auto-selected stocks:', error);
            }
          };
          insertLocksForAutoSelected();
        }
      }
    }
  }, [filteredStockItems, requestedQuantity, temporaryFormUniqueId, flag, createdBy, lockedStocksLoaded]);

  // Fetch stock data with locked stock IDs
  const fetchStockDataWithLocks = async (lockedIds = [], page = 1, batchSearch = null, append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setAlerts([]);
    try {
      const response = customFetchStock 
        ? await customFetchStock(productId, branchId, businessId)
        : await getStockByProduct(productId, businessId, branchId, page, pageSize, batchSearch);
      
      if (response.success) {
        const fetchedStockItems = response.stockItems || [];
        const unlockedStockItems = fetchedStockItems.filter(item => {
          const isFractional = item.unit_AllowFractional || item.unitAllowFractional || item.Unit_AllowFractional;
          return isFractional || !lockedIds.includes(item.purchaseStock_Id);
        });
        
        if (append) {
          setStockItems(prev => {
            const existingIds = new Set(prev.map(item => item.purchaseStock_Id));
            const newUniqueItems = unlockedStockItems.filter(item => !existingIds.has(item.purchaseStock_Id));
            return [...prev, ...newUniqueItems];
          });
          setDisplayCount(prev => prev + unlockedStockItems.length);
        } else {
          setStockItems(unlockedStockItems);
          setDisplayCount(Math.min(initialDisplayCount, unlockedStockItems.length));
        }
        
        const totalPagesValue = response.totalPages || 1;
        const totalRecordsValue = response.totalRecords || response.totalAvailableStock || 0;
        setCurrentPage(page);
        setTotalPages(totalPagesValue);
        setTotalRecords(totalRecordsValue);
        setHasMorePages(page < totalPagesValue);
        
        if (!append) {
          const newAlerts = [];
          if (response.hasExpiredStock) {
            newAlerts.push({
              type: 'warning',
              message: 'Some stock items have expired. Please check expiry dates carefully.'
            });
          }
          if (response.hasCloseToExpiryStock) {
            newAlerts.push({
              type: 'info',
              message: 'Some stock items are close to expiry (within 30 days).'
            });
          }
          let isShort = false;
          let displayAvailable = response.totalAvailableStock;
          
          const localIsFractional = fetchedStockItems.some(item => item.unit_AllowFractional || item.unitAllowFractional || item.Unit_AllowFractional) || isFractionalProduct;
          
          if (localIsFractional) {
            const sumOnPage = fetchedStockItems.reduce((acc, item) => acc + (parseFloat(item.purchaseStock_RemainingQty || item.purchaseStock_Quantity) || 0), 0);
            if (sumOnPage < requestedQuantity && totalPagesValue <= 1) {
              isShort = true;
              displayAvailable = sumOnPage;
            } else if (sumOnPage < requestedQuantity) {
              isShort = false;
            }
          } else {
            isShort = response.totalAvailableStock < requestedQuantity;
          }

          if (isShort) {
            const unitLabel = localIsFractional ? 'quantity' : 'stock items';
            const formattedAvailable = localIsFractional && displayAvailable % 1 !== 0 ? displayAvailable.toFixed(2) : displayAvailable;
            newAlerts.push({
              type: localIsFractional ? 'warning' : 'danger',
              message: localIsFractional
                ? `Note: Total available stock (${formattedAvailable}) is less than the requested quantity (${requestedQuantity}). You can allocate a partial amount.`
                : `Only ${formattedAvailable} ${unitLabel} available, but ${requestedQuantity} requested.`
            });
          }
          setAlerts(newAlerts);
        }
      } else {
        if (!append) {
          setStockItems([]);
          setAlerts([{ type: 'info', message: response.message || 'No stock available' }]);
        }
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error loading stock data';
      if (!append) {
        setAlerts([{ type: 'danger', message: errorMessage }]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchStockData = async () => {
    await fetchStockDataWithLocks(lockedStockIds, currentPage, searchTerm || null, false);
  };

  const handleItemToggle = async (stockId) => {
    const isCurrentlySelected = selectedItems.includes(stockId);
    const item = stockItems.find(s => s.purchaseStock_Id === stockId);
    const remainingQty = item ? (item.purchaseStock_RemainingQty || item.purchaseStock_Quantity || 1) : 1;
    const isFractional = item ? (item.unit_AllowFractional || item.unitAllowFractional || item.Unit_AllowFractional) : false;

    console.log('🔒 Stock toggle:', {
      stockId,
      isCurrentlySelected,
      temporaryFormUniqueId,
      flag,
      createdBy,
      isFractional,
      remainingQty,
      hasAllProps: !!(temporaryFormUniqueId && flag && createdBy)
    });
    
    if (isCurrentlySelected) {
      setSelectedItems(prev => prev.filter(id => id !== stockId));
      setSelectedQuantities(prev => {
        const copy = { ...prev };
        delete copy[stockId];
        return copy;
      });
      
      if (temporaryFormUniqueId && flag && createdBy && !isFractional) {
        try {
          console.log('🔒 Deleting lock for stock:', stockId);
          await deleteStockSelectionLock(temporaryFormUniqueId, stockId.toString());
          console.log('✅ Lock deleted successfully');
        } catch (error) {
          console.error('❌ Error deleting stock lock:', error);
        }
      }
    } else {
      setSelectedItems(prev => [...prev, stockId]);
      
      let defaultQty = 1;
      if (isFractional) {
        const currentTotalSelected = selectedItems.reduce((sum, id) => sum + (selectedQuantities[id] || 0), 0);
        const needed = Math.max(0, requestedQuantity - currentTotalSelected);
        defaultQty = Math.min(needed > 0 ? needed : remainingQty, remainingQty);
      }
      
      setSelectedQuantities(prev => ({
        ...prev,
        [stockId]: defaultQty
      }));
      
      if (temporaryFormUniqueId && flag && createdBy && !isFractional) {
        try {
          console.log('🔒 Inserting lock for stock:', {
            stockId,
            temporaryFormUniqueId,
            flag,
            createdBy
          });
          const result = await insertStockSelectionLock(
            stockId.toString(),
            temporaryFormUniqueId,
            flag,
            createdBy
          );
          
          console.log('🔒 Lock insert result:', result);
          
          if (!result.isSuccess) {
            setSelectedItems(prev => prev.filter(id => id !== stockId));
            setSelectedQuantities(prev => {
              const copy = { ...prev };
              delete copy[stockId];
              return copy;
            });
            
            setLockedStockIds(prev => {
              if (!prev.includes(stockId)) {
                return [...prev, stockId];
              }
              return prev;
            });
            
            const errorMessage = result.message || 'This stock is already locked by another user';
            setAlerts(prev => {
              const hasSimilarAlert = prev.some(alert => 
                alert.message === errorMessage && alert.type === 'danger'
              );
              
              if (!hasSimilarAlert) {
                const newAlert = {
                  type: 'danger',
                  message: errorMessage,
                  id: `lock-failed-${Date.now()}-${Math.random()}`
                };
                
                setTimeout(() => {
                  setDismissedAlerts(prev => new Set([...prev, newAlert.id]));
                  setAlerts(current => current.filter(alert => alert.id !== newAlert.id));
                }, 5000);
                
                return [...prev, newAlert];
              }
              return prev;
            });
            
            await fetchLockedStocks();
          } else {
            console.log('✅ Lock inserted successfully');
          }
        } catch (error) {
          console.error('❌ Error inserting stock lock:', error);
          setSelectedItems(prev => prev.filter(id => id !== stockId));
          setSelectedQuantities(prev => {
            const copy = { ...prev };
            delete copy[stockId];
            return copy;
          });
          
          setLockedStockIds(prev => {
            if (!prev.includes(stockId)) {
              return [...prev, stockId];
            }
            return prev;
          });
          
          const errorMessage = 'Failed to lock stock. It may be locked by another user.';
          setAlerts(prev => {
            const hasSimilarAlert = prev.some(alert => 
              alert.message === errorMessage && alert.type === 'danger'
            );
            
            if (!hasSimilarAlert) {
              const newAlert = {
                type: 'danger',
                message: errorMessage,
                id: `lock-error-${Date.now()}-${Math.random()}`
              };
              
              setTimeout(() => {
                setDismissedAlerts(prev => new Set([...prev, newAlert.id]));
                setAlerts(current => current.filter(alert => alert.id !== newAlert.id));
              }, 5000);
              
              return [...prev, newAlert];
            }
            return prev;
          });
          
          await fetchLockedStocks();
        }
      }
    }
  };

  const handleQuantityChange = (stockId, value) => {
    if (value === '') {
      setSelectedQuantities(prev => ({
        ...prev,
        [stockId]: ''
      }));
      return;
    }

    const floatVal = parseFloat(value);
    const item = stockItems.find(s => s.purchaseStock_Id === stockId);
    const maxQty = item ? (item.purchaseStock_RemainingQty || item.purchaseStock_Quantity || 99999) : 99999;
    
    if (!isNaN(floatVal) && floatVal > maxQty) {
      setSelectedQuantities(prev => ({
        ...prev,
        [stockId]: maxQty
      }));
    } else {
      setSelectedQuantities(prev => ({
        ...prev,
        [stockId]: value
      }));
    }

    if (!selectedItems.includes(stockId)) {
      handleItemToggle(stockId);
    }
  };

  const handleShowMore = async () => {
    if (hasMorePages && !isLoadingMore) {
      const nextPage = currentPage + 1;
      await fetchStockDataWithLocks(lockedStockIds, nextPage, searchTerm || null, true);
    }
  };

  const handleConfirm = () => {
    const selectedStockData = stockItems.filter(item => 
      selectedItems.includes(item.purchaseStock_Id) &&
      (parseFloat(selectedQuantities[item.purchaseStock_Id]) || 0) > 0
    );
    
    // Group by sales price AND discount percentage
    const groupedStock = {};
    selectedStockData.forEach(stock => {
      const price = stock.purchaseStock_SalesPrice || 0;
      const discount = stock.discountPercent || 0;
      const key = `${price}_${discount}`;
      
      if (!groupedStock[key]) {
        groupedStock[key] = [];
      }
      
      // For fractional items use the exact qty entered by user; for Each items default to 1
      const isFractional = stock.unit_AllowFractional || stock.unitAllowFractional || stock.Unit_AllowFractional;
      const qty = isFractional
        ? (parseFloat(selectedQuantities[stock.purchaseStock_Id]) || 0)
        : 1;

      groupedStock[key].push({
        ...stock,
        selectedQty: qty  // This is consumed by BillingPage/QuickInvoicePage to build stockId:qty string
      });
    });

    // Convert to final format
    const finalStockData = Object.entries(groupedStock).map(([key, stocks]) => {
      const [price, discount] = key.split('_');
      const isFractionalGroup = stocks.some(s => s.unit_AllowFractional || s.unitAllowFractional || s.Unit_AllowFractional);
      const totalQty = isFractionalGroup
        ? stocks.reduce((sum, s) => sum + (s.selectedQty || 0), 0)  // sum weights
        : stocks.length;                                              // count rows for Each
      return {
        price: parseFloat(price),
        discount: parseFloat(discount),
        quantity: totalQty,
        stocks: stocks,
        batchNumbers: stocks.map(s => s.purchaseStock_BatchNo).join(', '),
        expiryDates: stocks.map(s => s.purchaseStock_ExpDate).join(', ')
      };
    });

    onStockSelected(finalStockData);
    toggle();
  };

  const handleCleanupLocks = async () => {
    if (temporaryFormUniqueId && selectedItems.length > 0) {
      try {
        const stockIdsString = selectedItems.join(',');
        await deleteStockSelectionLock(temporaryFormUniqueId, stockIdsString);
      } catch (error) {
        console.error('Error cleaning up locks on cancel:', error);
      }
    }
  };

  const handleCancel = () => {
    handleCleanupLocks();
    toggle();
  };

  const getDisplayItems = () => {
    return filteredStockItems;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const dateObj = parseDDMMYYYY(dateStr);
      if (!dateObj) return dateStr;
      return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isExpired = (expDate) => {
    if (!expDate) return false;
    const dateObj = parseDDMMYYYY(expDate);
    if (!dateObj) return false;
    return dateObj < new Date();
  };

  const isCloseToExpiry = (expDate) => {
    if (!expDate) return false;
    const dateObj = parseDDMMYYYY(expDate);
    if (!dateObj) return false;
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return dateObj <= thirtyDaysFromNow && dateObj >= new Date();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <Package className="me-2" size={20} />
        Stock Selection - {productName}
      </ModalHeader>
      <style>{`
        .stock-selection-modal .badge,
        .stock-selection-modal .badge.bg-danger,
        .stock-selection-modal .badge.bg-warning,
        .stock-selection-modal .badge.bg-success,
        .stock-selection-modal span.badge,
        .stock-selection-modal td .badge {
          font-size: 0.875rem !important;
          line-height: 1.2 !important;
          padding: 0.25em 0.4em !important;
        }
        .stock-selection-modal table,
        .stock-selection-modal table th,
        .stock-selection-modal table td,
        .stock-selection-modal table tbody,
        .stock-selection-modal table thead,
        .stock-selection-modal table td span.badge {
          font-size: 0.875rem !important;
        }
      `}</style>
      <ModalBody className="stock-selection-modal" style={{ fontSize: '0.875rem' }}>
        {alerts
          .filter(alert => !dismissedAlerts.has(alert.id))
          .map((alert, index) => (
            <Alert 
              key={alert.id || index} 
              color={alert.type} 
              className="mb-3"
              fade={true}
              style={{ fontSize: '0.875rem' }}
            >
              {alert.message}
            </Alert>
          ))}

        <FormGroup>
          <Label for="batchSearch" style={{ fontSize: '0.875rem' }}>Search by Batch Number</Label>
          <InputGroup>
            <InputGroupText>
              <Search size={16} />
            </InputGroupText>
            <Input
              id="batchSearch"
              type="text"
              placeholder="Enter batch number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </InputGroup>
        </FormGroup>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3" style={{ fontSize: '0.875rem', color: '#6c757d' }}>
              Loading stock data...
            </div>
          </div>
        ) : (
          <>
            <Table responsive striped style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={{ fontSize: '0.875rem' }}>Select</th>
                  <th style={{ fontSize: '0.875rem' }}>Batch No</th>
                  <th style={{ fontSize: '0.875rem' }}>Expiry Date</th>
                  {isFractionalProduct && <th style={{ fontSize: '0.875rem' }}>Remaining Qty</th>}
                  {isFractionalProduct && <th style={{ fontSize: '0.875rem' }}>Qty to Sell</th>}
                  {showCostPriceColumn && <th style={{ fontSize: '0.875rem' }}>Cost Price</th>}
                  <th style={{ fontSize: '0.875rem' }}>Sales Price</th>
                  <th style={{ fontSize: '0.875rem' }}>Discount %</th>
                  <th style={{ fontSize: '0.875rem' }}>Status</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '0.875rem' }}>
                {getDisplayItems().map((item) => {
                  const remainingQty = item.purchaseStock_RemainingQty || item.purchaseStock_Quantity || 0;
                  return (
                    <tr key={item.purchaseStock_Id} style={{ fontSize: '0.875rem' }}>
                      <td style={{ fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.purchaseStock_Id)}
                          onChange={() => handleItemToggle(item.purchaseStock_Id)}
                          disabled={lockedStockIds.includes(item.purchaseStock_Id)}
                          className="form-check-input"
                          title={lockedStockIds.includes(item.purchaseStock_Id) ? 'This stock is locked by another user' : ''}
                        />
                      </td>
                      <td style={{ fontSize: '0.875rem' }}>{item.purchaseStock_BatchNo || 'N/A'}</td>
                      <td style={{ fontSize: '0.875rem' }}>
                        <span className={isExpired(item.purchaseStock_ExpDate) ? 'text-danger' : 
                                     isCloseToExpiry(item.purchaseStock_ExpDate) ? 'text-warning' : ''}
                              style={{ fontSize: '0.875rem' }}>
                          {formatDate(item.purchaseStock_ExpDate)}
                        </span>
                      </td>
                      {isFractionalProduct && (
                        <td style={{ fontSize: '0.875rem' }}>
                          {remainingQty.toFixed(4)}
                        </td>
                      )}
                      {isFractionalProduct && (
                        <td style={{ fontSize: '0.875rem', width: '120px' }}>
                          <Input
                            type="number"
                            step="any"
                            min="0"
                            max={remainingQty}
                            value={selectedQuantities[item.purchaseStock_Id] !== undefined ? selectedQuantities[item.purchaseStock_Id] : ''}
                            onChange={(e) => handleQuantityChange(item.purchaseStock_Id, e.target.value)}
                            disabled={lockedStockIds.includes(item.purchaseStock_Id)}
                            style={{ fontSize: '0.875rem', padding: '2px 5px', height: '28px' }}
                            placeholder="0.00"
                          />
                        </td>
                      )}
                      {showCostPriceColumn && (
                        <td style={{ fontSize: '0.875rem' }}>{(item.purchaseStock_CostPrice || 0).toFixed(2)}</td>
                      )}
                      <td style={{ fontSize: '0.875rem' }}>{(item.purchaseStock_SalesPrice || 0).toFixed(2)}</td>
                      <td style={{ fontSize: '0.875rem' }}>{(item.discountPercent || 0).toFixed(2)}%</td>
                      <td style={{ fontSize: '0.875rem' }}>
                        {isExpired(item.purchaseStock_ExpDate) && (
                          <span className="badge bg-danger" style={{ fontSize: '0.875rem', lineHeight: '1.2', padding: '0.25em 0.4em' }}>Expired</span>
                        )}
                        {!isExpired(item.purchaseStock_ExpDate) && isCloseToExpiry(item.purchaseStock_ExpDate) && (
                          <span className="badge bg-warning" style={{ fontSize: '0.875rem', lineHeight: '1.2', padding: '0.25em 0.4em' }}>Close to Expiry</span>
                        )}
                        {!isExpired(item.purchaseStock_ExpDate) && !isCloseToExpiry(item.purchaseStock_ExpDate) && (
                          <span className="badge bg-success" style={{ fontSize: '0.875rem', lineHeight: '1.2', padding: '0.25em 0.4em' }}>Good</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {hasMorePages && (
              <div className="text-center mt-3">
                <Button 
                  color="primary" 
                  outline 
                  onClick={handleShowMore} 
                  disabled={isLoadingMore}
                  style={{ fontSize: '0.875rem' }}
                >
                  {isLoadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading...
                    </>
                  ) : (
                    `Load More (Page ${currentPage + 1} of ${totalPages} - ${totalRecords} total items)`
                  )}
                </Button>
                <div className="mt-2 text-muted" style={{ fontSize: '0.75rem' }}>
                  Currently showing {filteredStockItems.length} of {totalRecords} items
                </div>
              </div>
            )}

            {selectedItems.length > 0 && (
              <div className="mt-3 p-2 bg-light rounded" style={{ fontSize: '0.875rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>
                  {isFractionalProduct
                    ? `Total Weight Selected: ${selectedItems.reduce((sum, id) => sum + (parseFloat(selectedQuantities[id]) || 0), 0).toFixed(4)}`
                    : `Selected Items: ${selectedItems.length}`
                  }
                </strong>
                <br />
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                  {isFractionalProduct
                    ? <>Requested: {requestedQuantity} | Allocated: {selectedItems.reduce((sum, id) => sum + (parseFloat(selectedQuantities[id]) || 0), 0).toFixed(4)} | Batches used: {selectedItems.length}</>
                    : <>Requested: {requestedQuantity} | Selected: {selectedItems.length}</>
                  }
                  {totalRecords > 0 && (
                    <> | Showing: {filteredStockItems.length} of {totalRecords} available</>
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter style={{ fontSize: '0.875rem' }}>
        <Button color="secondary" onClick={handleCancel} style={{ fontSize: '0.875rem' }}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleConfirm}
          disabled={selectedItems.length === 0 || (isFractionalProduct && selectedItems.reduce((sum, id) => sum + (parseFloat(selectedQuantities[id]) || 0), 0) <= 0)}
          style={{ fontSize: '0.875rem' }}
        >
          {isFractionalProduct
            ? `Confirm (${selectedItems.reduce((sum, id) => sum + (parseFloat(selectedQuantities[id]) || 0), 0).toFixed(4)} total)`
            : `Confirm Selection (${selectedItems.length} items)`
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default StockSelectionModal;
